import type { CurrencyAmount } from './types';

// How the platform's values reach people, the same in the web app and the phone app (TF-277): money
// in each currency, phone numbers to read and to dial, and an address on a map. Pure, no imports
// from either app; the web app's crm/format.ts, crm/phone.ts and crm/address/maps.ts re-export them.

const moneyFormats = new Map<string, Intl.NumberFormat>();

/** 12 500 € in Latvian, €12,500 in English; whole amounts without decimals. */
export function formatMoney(amount: number | null, currency: string, language: string): string {
  if (amount === null) return '—';
  const whole = Number.isInteger(amount);
  const key = `${language}|${currency}|${whole}`;
  let format = moneyFormats.get(key);
  if (!format) {
    try {
      format = new Intl.NumberFormat(language, { style: 'currency', currency, maximumFractionDigits: whole ? 0 : 2, minimumFractionDigits: whole ? 0 : 2 });
    } catch {
      format = new Intl.NumberFormat(language, { maximumFractionDigits: 2 });
    }
    moneyFormats.set(key, format);
  }
  return format.format(amount);
}

/** Amounts in several currencies on one line, never added together: "€10,000 · $5,000" (TF-36); empty for none. */
export const formatAmounts = (amounts: CurrencyAmount[] | undefined, language: string) =>
  (amounts ?? []).filter((one) => one.amount).map((one) => formatMoney(one.amount, one.currency, language)).join(' · ');

// Country calling codes are prefix-free: these are the one- and two-digit ones (ITU E.164); every
// other code has three digits. Enough to put the space in the right place after the code.
const CALLING_CODES_1_2 = new Set(['1', '7', '20', '27', '30', '31', '32', '33', '34', '36', '39', '40', '41', '43', '44', '45', '46', '47', '48', '49', '51', '52', '53', '54', '55', '56', '57', '58', '60', '61', '62', '63', '64', '65', '66', '81', '82', '84', '86', '90', '91', '92', '93', '94', '95', '98']);

/**
 * A phone number as people read it (TF-26): an international number typed as one run of digits,
 * "+37129123456", is shown grouped - "+371 2912 3456". Anything else (already spaced, a local
 * number, "ask reception") is shown exactly as typed.
 */
export function formatPhone(value: string): string {
  const match = /^\+(\d{8,15})$/.exec(value.trim());
  if (!match) return value;
  const digits = match[1]!;
  const codeLength = CALLING_CODES_1_2.has(digits.slice(0, 1)) ? 1 : CALLING_CODES_1_2.has(digits.slice(0, 2)) ? 2 : 3;
  const national = digits.slice(codeLength);
  const groups: string[] = [];
  // Fours when they divide it evenly, otherwise threes with the last group taking up to four.
  const size = national.length % 4 === 0 ? 4 : 3;
  let rest = national;
  while (rest.length > 4) {
    groups.push(rest.slice(0, size));
    rest = rest.slice(size);
  }
  groups.push(rest);
  return `+${digits.slice(0, codeLength)} ${groups.join(' ')}`;
}

// Dialling a phone number from the app (TF-34): the number as the phone or the computer's calling app
// needs it in a tel: link. Separators go, a leading 00 becomes +, and a number typed without a
// country code gets the company's (dropping the trunk prefix where the country has one), so it also
// rings from abroad and in Teams. What is shown stays as typed.

interface DialPlan {
  code: string;
  /** Written before a national number inside the country and dropped in the international form. */
  trunk?: string[];
}

const PLANS: Record<string, DialPlan> = {
  AT: { code: '43', trunk: ['0'] },
  BE: { code: '32', trunk: ['0'] },
  BG: { code: '359', trunk: ['0'] },
  CH: { code: '41', trunk: ['0'] },
  CY: { code: '357' },
  CZ: { code: '420' },
  DE: { code: '49', trunk: ['0'] },
  DK: { code: '45' },
  EE: { code: '372' },
  ES: { code: '34' },
  FI: { code: '358', trunk: ['0'] },
  FR: { code: '33', trunk: ['0'] },
  GB: { code: '44', trunk: ['0'] },
  GR: { code: '30' },
  HR: { code: '385', trunk: ['0'] },
  HU: { code: '36', trunk: ['06'] },
  IE: { code: '353', trunk: ['0'] },
  IS: { code: '354' },
  // Italy keeps the leading 0 of a landline in the international form: no trunk.
  IT: { code: '39' },
  LI: { code: '423' },
  // Lithuania's trunk prefix was 8 and is becoming 0: either is dropped.
  LT: { code: '370', trunk: ['8', '0'] },
  LU: { code: '352' },
  LV: { code: '371' },
  MT: { code: '356' },
  NL: { code: '31', trunk: ['0'] },
  NO: { code: '47' },
  PL: { code: '48' },
  PT: { code: '351' },
  RO: { code: '40', trunk: ['0'] },
  SE: { code: '46', trunk: ['0'] },
  SI: { code: '386', trunk: ['0'] },
  SK: { code: '421', trunk: ['0'] },
  US: { code: '1', trunk: ['1'] },
  CA: { code: '1', trunk: ['1'] },
  AU: { code: '61', trunk: ['0'] },
  CN: { code: '86', trunk: ['0'] },
  IN: { code: '91', trunk: ['0'] },
  JP: { code: '81', trunk: ['0'] },
  SG: { code: '65' },
};

/**
 * The number for a tel: link, or null when nothing diallable is left. `country` is the company's
 * (ISO 3166-1 alpha-2); without it, or for a country not in the table, a national number is dialled
 * as typed.
 */
export function dialNumber(raw: string, country?: string | null): string | null {
  const compact = raw.trim().replace(/[\s\-.()/ ]/g, '');
  if (compact.startsWith('+')) {
    const digits = compact.slice(1).replace(/\D/g, '');
    return digits ? `+${digits}` : null;
  }
  const digits = compact.replace(/\D/g, '');
  if (!digits) return null;
  if (digits.startsWith('00')) return digits.length > 2 ? `+${digits.slice(2)}` : null;
  const plan = country ? PLANS[country] : undefined;
  if (!plan) return digits;
  const trunk = plan.trunk?.find((prefix) => digits.startsWith(prefix) && digits.length > prefix.length + 5);
  return `+${plan.code}${trunk ? digits.slice(trunk.length) : digits}`;
}

// An address on a map (TF-32): links only, built when they are shown and followed when pressed, so
// nothing is asked of a map provider before somebody wants it.

/** Where "Show on map" opens on a computer: Google Maps; OpenStreetMap as a company's choice (TF-40). */
export type MapProvider = 'google' | 'osm';

/** On a computer: the address in a map, in a new tab. */
export function mapUrl(query: string, provider: MapProvider = 'google'): string {
  const text = encodeURIComponent(query);
  return provider === 'osm' ? `https://www.openstreetmap.org/search?query=${text}` : `https://www.google.com/maps/search/?api=1&query=${text}`;
}

/** On Android: the system asks which navigation app to use (Google Maps, Waze...). */
export const geoUrl = (query: string) => `geo:0,0?q=${encodeURIComponent(query)}`;

export type NavigationApp = 'apple' | 'google' | 'waze';

/** On an iPhone, a choice of apps: each link opens the app when it is installed, or its web version. */
export function navigationLinks(query: string): { app: NavigationApp; url: string }[] {
  const text = encodeURIComponent(query);
  return [
    { app: 'apple', url: `https://maps.apple.com/?q=${text}` },
    { app: 'google', url: `https://www.google.com/maps/search/?api=1&query=${text}` },
    { app: 'waze', url: `https://waze.com/ul?q=${text}&navigate=yes` },
  ];
}
